import React, { useState, useEffect, useCallback } from 'react'
import { Eye, EyeOff, Loader2, Moon, Sun, Shield } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '@/context/ThemeContext'
import Cookies from 'js-cookie'
import * as adminApi from '@/features/admin/services/adminApi'
import { setAuthToken } from '@/lib/authToken'

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const segment = parts[1]
    if (!segment) return null
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
    return JSON.parse(atob(padded)) as Record<string, unknown>
  } catch {
    return null
  }
}

// Auth login via existing endpoint
async function authLogin(payload: {
  email: string
  password: string
}): Promise<{ access_token: string; user: Record<string, unknown> }> {
  const API_BASE = (import.meta.env?.VITE_API_URL as string) || ''
  function buildUrl(path: string) {
    if (API_BASE) return `${API_BASE.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`
    return path.startsWith('/') ? path : `/${path}`
  }
  const res = await fetch(buildUrl('/api/auth/login'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload) as string,
  })
  if (!res.ok) {
    const data = await res.json().catch(() => null)
    const msg =
      (data?.message as string) ||
      (data?.error as string) ||
      res.statusText ||
      'Login failed'
    throw new Error(msg)
  }
  return res.json() as Promise<{ access_token: string; user: Record<string, unknown> }>
}

type Mode = 'login' | 'register'

const AdminLoginPage: React.FC = () => {
  const [mode, setMode] = useState<Mode>('login')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const { isDarkMode, setIsDarkMode, currentTheme } = useTheme()
  const navigate = useNavigate()

  // Redirect if already logged in as super_admin
  useEffect(() => {
    const token = Cookies.get('token')
    if (token) {
      const payload = decodeJwtPayload(token)
      if (payload?.platformRole === 'super_admin') {
        navigate('/admin', { replace: true })
      }
    }
  }, [navigate])

  const resetForm = useCallback(() => {
    setError('')
    setName('')
    setPassword('')
    setConfirmPassword('')
  }, [])

  const toggleMode = useCallback(() => {
    setMode((prev) => (prev === 'login' ? 'register' : 'login'))
    resetForm()
    setEmail('')
  }, [resetForm])

  const validateEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)

  const handleLogin = async (evt: React.FormEvent) => {
    evt.preventDefault()
    setError('')

    if (!email.trim() || !validateEmail(email.trim())) {
      setError('Please enter a valid email address.')
      return
    }
    if (!password.trim()) {
      setError('Password is required.')
      return
    }

    setLoading(true)
    try {
      const result = await authLogin({ email: email.trim(), password })
      const payload = decodeJwtPayload(result.access_token)
      if (payload?.platformRole !== 'super_admin') {
        setError('You do not have admin access.')
        return
      }
      setAuthToken(result.access_token)
      try {
        localStorage.setItem('user', JSON.stringify(result.user))
      } catch {}
      navigate('/admin', { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to sign in. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleRegister = async (evt: React.FormEvent) => {
    evt.preventDefault()
    setError('')

    if (!name.trim() || name.trim().length < 2) {
      setError('Name must be at least 2 characters.')
      return
    }
    if (!email.trim() || !validateEmail(email.trim())) {
      setError('Please enter a valid email address.')
      return
    }
    if (!password.trim() || password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }

    setLoading(true)
    try {
      const result = await adminApi.adminRegister({
        name: name.trim(),
        email: email.trim(),
        password,
        confirm_password: confirmPassword,
      })
      setAuthToken(result.access_token)
      try {
        localStorage.setItem('user', JSON.stringify(result.user))
      } catch {}
      navigate('/admin', { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to register. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const isLogin = mode === 'login'

  return (
    <div
      className="min-h-screen transition-colors duration-300"
      style={{ backgroundColor: currentTheme.primary }}
    >
      <div className="relative mx-auto flex min-h-screen max-w-[1480px] items-center px-4 py-8 xl:px-8">
        {/* Theme toggle */}
        <button
          type="button"
          onClick={() => setIsDarkMode(!isDarkMode)}
          className="absolute right-4 top-4 z-10 inline-flex items-center justify-center rounded-full border p-3 shadow-sm transition"
          style={{
            backgroundColor: currentTheme.secondary,
            color: currentTheme.text,
            borderColor: currentTheme.border,
          }}
          aria-label="Toggle theme"
        >
          {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        <div className="grid w-full gap-8 xl:grid-cols-[1.1fr_0.9fr] items-stretch">
          {/* Form panel */}
          <section
            className="overflow-hidden rounded-[32px] border shadow-[0_32px_80px_rgba(15,23,42,0.08)] min-h-[560px]"
            style={{
              boxShadow: currentTheme.boxShadow,
              backgroundColor: currentTheme.secondary,
              borderColor: currentTheme.border,
            }}
          >
            <div className="p-8 sm:p-10 xl:p-12">
              {/* Heading */}
              <div className="mb-6 flex flex-col gap-3">
                <div>
                  <h1
                    className="text-4xl font-semibold sm:text-5xl"
                    style={{ color: currentTheme.text }}
                  >
                    {isLogin ? 'Admin sign in' : 'Create admin account'}
                  </h1>
                  <p
                    className="mt-2 max-w-2xl text-base leading-7"
                    style={{ color: currentTheme.secondrytext }}
                  >
                    {isLogin
                      ? 'Sign in to the Zeph AI admin portal.'
                      : 'Register the first super admin account.'}
                  </p>
                </div>
              </div>

              {/* Error banner */}
              {error && (
                <div
                  className="mb-6 rounded-3xl px-5 py-4 text-sm"
                  style={{
                    backgroundColor: currentTheme.errorBackground,
                    color: currentTheme.errorText,
                    border: `1px solid ${currentTheme.border}`,
                  }}
                >
                  {error}
                </div>
              )}

              {/* Form */}
              <form
                onSubmit={isLogin ? handleLogin : handleRegister}
                className="space-y-4 min-h-[320px]"
              >
                {/* Name (register only) */}
                {!isLogin && (
                  <div>
                    <label
                      className="block text-sm font-semibold mb-2"
                      style={{ color: currentTheme.text }}
                    >
                      Full name
                    </label>
                    <input
                      value={name}
                      onChange={(e) => { setName(e.target.value); setError('') }}
                      placeholder="Jane Smith"
                      className="w-full rounded-3xl border px-4 py-3 text-sm outline-none transition"
                      style={{
                        backgroundColor: currentTheme.inputBackground,
                        color: currentTheme.text,
                        borderColor: currentTheme.border,
                      }}
                    />
                  </div>
                )}

                {/* Email */}
                <div>
                  <label
                    className="block text-sm font-semibold mb-2"
                    style={{ color: currentTheme.text }}
                  >
                    Email address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError('') }}
                    placeholder="admin@example.com"
                    className="w-full rounded-3xl border px-4 py-3 text-sm outline-none transition"
                    style={{
                      backgroundColor: currentTheme.inputBackground,
                      color: currentTheme.text,
                      borderColor: currentTheme.border,
                    }}
                  />
                </div>

                {/* Password */}
                <div className={!isLogin ? 'grid gap-5 sm:grid-cols-2' : ''}>
                  <div>
                    <label
                      className="block text-sm font-semibold mb-2"
                      style={{ color: currentTheme.text }}
                    >
                      Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => { setPassword(e.target.value); setError('') }}
                        placeholder="••••••••"
                        className="w-full rounded-3xl border px-4 py-3 pr-12 text-sm outline-none transition"
                        style={{
                          backgroundColor: currentTheme.inputBackground,
                          color: currentTheme.text,
                          borderColor: currentTheme.border,
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((p) => !p)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 inline-flex h-8 w-8 items-center justify-center rounded-full transition"
                        style={{
                          color: currentTheme.secondrytext,
                          backgroundColor: currentTheme.secondary,
                        }}
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  {/* Confirm password (register only) */}
                  {!isLogin && (
                    <div>
                      <label
                        className="block text-sm font-semibold mb-2"
                        style={{ color: currentTheme.text }}
                      >
                        Confirm password
                      </label>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          value={confirmPassword}
                          onChange={(e) => { setConfirmPassword(e.target.value); setError('') }}
                          placeholder="••••••••"
                          className="w-full rounded-3xl border px-4 py-3 pr-12 text-sm outline-none transition"
                          style={{
                            backgroundColor: currentTheme.inputBackground,
                            color: currentTheme.text,
                            borderColor: currentTheme.border,
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword((p) => !p)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 inline-flex h-8 w-8 items-center justify-center rounded-full transition"
                          style={{
                            color: currentTheme.secondrytext,
                            backgroundColor: currentTheme.secondary,
                          }}
                        >
                          {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="flex w-full items-center justify-center gap-2 rounded-3xl px-5 py-3 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60"
                  style={{
                    backgroundColor: currentTheme.sendButtonPrimary,
                    color: currentTheme.buttonText,
                    border: `1px solid ${currentTheme.border}`,
                  }}
                >
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
                  <span>{loading ? 'Processing...' : isLogin ? 'Sign in' : 'Create account'}</span>
                </button>
              </form>

              {/* Mode toggle */}
              <div className="mt-4 text-center text-sm">
                <p style={{ color: currentTheme.secondrytext }}>
                  {isLogin ? 'Need to register the first admin?' : 'Already have an admin account?'}
                  <button
                    type="button"
                    onClick={toggleMode}
                    className="ml-2 font-semibold underline transition"
                    style={{ color: currentTheme.text }}
                  >
                    {isLogin ? 'Register' : 'Sign in'}
                  </button>
                </p>
              </div>
            </div>
          </section>

          {/* Hero aside */}
          <aside
            className="relative overflow-hidden rounded-[32px] shadow-[0_32px_80px_rgba(15,23,42,0.25)] hidden xl:block"
            style={{ backgroundColor: currentTheme.sidebarBg }}
          >
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-6 p-10">
              <div
                className="flex items-center justify-center w-20 h-20 rounded-2xl"
                style={{ backgroundColor: currentTheme.accent }}
              >
                <Shield size={40} color="#fff" />
              </div>
              <div className="text-center">
                <h2 className="text-3xl font-bold text-white">Admin Portal</h2>
                <p className="mt-2 text-sm" style={{ color: currentTheme.sidebarText }}>
                  Manage users, workspaces, and platform settings from one place.
                </p>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  )
}

export default AdminLoginPage
